import { useRef, useState } from 'react';
import { ImageIcon, Music, Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDeleteContentMedia, useUploadContentMedia } from '@/hooks/use-content';
import { toast } from 'sonner';
import { toToastMessage } from '@/lib/api-error';
import type { ContentMediaAsset } from '@/types/content';

// SPEC-021: MediaUploader drives the three-step presigned upload flow
// for images and audio. The caller owns the list of attached assets and
// passes them in for display; the component emits newly registered
// assets via onUploaded so the parent can mutate its local state.

interface MediaUploaderProps {
  contentId: string;
  assets: ContentMediaAsset[];
  onUploaded?: (asset: ContentMediaAsset) => void;
  onDeleted?: (assetId: string) => void;
  acceptImages?: boolean;
  acceptAudio?: boolean;
  disabled?: boolean;
}

export function MediaUploader({
  contentId,
  assets,
  onUploaded,
  onDeleted,
  acceptImages = true,
  acceptAudio = true,
  disabled = false,
}: MediaUploaderProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<string | null>(null);
  const upload = useUploadContentMedia();
  const del = useDeleteContentMedia();

  const handleFile = async (file: File, assetType: 'image' | 'audio') => {
    setPending(file.name);
    try {
      // Image dimensions probe (optional enrichment).
      let width: number | undefined;
      let height: number | undefined;
      let durationSec: number | undefined;
      if (assetType === 'image') {
        const dims = await readImageDimensions(file).catch(() => null);
        if (dims) {
          width = dims.width;
          height = dims.height;
        }
      }
      if (assetType === 'audio') {
        const dur = await readAudioDuration(file).catch(() => null);
        if (dur !== null) durationSec = dur;
      }
      const asset = await upload.mutateAsync({
        contentId,
        file,
        assetType,
        width,
        height,
        durationSec,
      });
      toast.success(`Uploaded ${file.name}`);
      onUploaded?.(asset);
    } catch (err) {
      toast.error(toToastMessage(err, 'Upload failed'));
    } finally {
      setPending(null);
    }
  };

  const handleDelete = (asset: ContentMediaAsset) => {
    if (!window.confirm(`Delete ${asset.s3_object_key.split('/').pop()}?`)) return;
    del.mutate(
      { contentId, assetId: asset.id },
      {
        onSuccess: () => {
          toast.success('Asset removed');
          onDeleted?.(asset.id);
        },
        onError: (err) => toast.error(toToastMessage(err, 'Delete failed')),
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {acceptImages && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || !!pending}
            onClick={() => imageInputRef.current?.click()}
          >
            <ImageIcon className="mr-2 h-4 w-4" />
            Upload image
          </Button>
        )}
        {acceptAudio && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || !!pending}
            onClick={() => audioInputRef.current?.click()}
          >
            <Music className="mr-2 h-4 w-4" />
            Upload audio
          </Button>
        )}
        {pending && (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading {pending}…
          </span>
        )}
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file, 'image');
          e.target.value = '';
        }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file, 'audio');
          e.target.value = '';
        }}
      />

      {assets.length > 0 && (
        <ul className="divide-y rounded-md border">
          {assets.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              {a.asset_type === 'image' ? (
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Music className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="flex-1 truncate">
                <p className="truncate font-mono text-xs">{a.s3_object_key}</p>
                <p className="text-xs text-muted-foreground">
                  {a.mime_type} · {formatBytes(a.size_bytes)}
                  {a.duration_sec ? ` · ${a.duration_sec}s` : ''}
                  {a.width && a.height ? ` · ${a.width}×${a.height}` : ''}
                </p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => handleDelete(a)}
                disabled={disabled}
                title="Remove"
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {!contentId && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Upload className="mt-0.5 h-3 w-3" />
          Save the draft first to enable media uploads.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('could not read image'));
    };
    img.src = url;
  });
}

function readAudioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const dur = Number.isFinite(audio.duration) ? Math.round(audio.duration) : null;
      URL.revokeObjectURL(url);
      resolve(dur);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    audio.src = url;
  });
}
